import { isNative } from "@/lib/isNativePlatform";
import { parseVersion } from "./compareVersions";

// Version NATIVE de l'app (CFBundleShortVersionString, versionName) : la seule
// que la boutique connaisse. Même source que src/hooks/useAppVersion.ts.
//
// `pending` porte la lecture en cours (deux appels simultanés la partagent),
// puis reste en place quand elle a réussi : un succès est mémorisé pour toute
// la session, comme le null du web. Un ÉCHEC (plugin pas prêt, version hors
// format) n'est pas mémorisé : sinon une seule lecture ratée au démarrage
// priverait la session entière de sa version native.
let pending: Promise<string | null> | null = null;

/** Null sur le web, si le plugin manque ou si la version sort du format. Ne jette jamais. */
export function getNativeAppVersion(): Promise<string | null> {
  if (!pending) {
    const attempt: Promise<string | null> = readOnce().then((result) => {
      // Échec : le prochain appel réessaie. Les appels déjà en attente
      // reçoivent quand même ce null, sans relancer de lecture.
      if (result.failed && pending === attempt) pending = null;
      return result.version;
    });
    pending = attempt;
  }
  return pending;
}

/** Une lecture. `failed` distingue l'échec (à réessayer) du null du web (définitif). */
async function readOnce(): Promise<{ version: string | null; failed: boolean }> {
  if (!isNative()) return { version: null, failed: false };
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    if (parseVersion(info?.version)) return { version: info.version, failed: false };
  } catch {
    // Traité comme une version illisible, juste dessous.
  }
  return { version: null, failed: true };
}

export function resetNativeAppVersionForTests(): void {
  pending = null;
}
